// Site localization. Translations live inline at the point of use — templates
// carry both languages and a Handlebars gadget picks one:
//
//   {{tr "Book a slot" "Reserva un horario"}}          short plain strings
//   {{#en}}...{{/en}}{{#es}}...{{/es}}                 text with markup/vars
//
// Client code uses tr() from this module the same way: tr('Save', 'Guardar').
//
// This module is imported by both the server and the browser bundles, so it
// must stay free of Node-only dependencies.

export type Locale = 'en' | 'es';

export function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'es';
}

// Pick the variant for a locale. Generic so client code can select between
// JSX fragments as well as strings.
export function localized<T>(locale: Locale, en: T, es: T): T {
  return locale === 'es' ? es : en;
}

// BCP 47 tag for Date/number formatting APIs.
export function dateLocale(locale: Locale): string {
  return locale === 'es' ? 'es-MX' : 'en-US';
}

// Cookie first (explicit user choice), then the browser's primary language.
export function resolveLocale(cookieLang: string | undefined, acceptLanguage: string | undefined): Locale {
  if (isLocale(cookieLang)) return cookieLang;
  const primary = (acceptLanguage ?? '').split(',')[0]?.trim().toLowerCase() ?? '';
  return primary.startsWith('es') ? 'es' : 'en';
}

// Browser-only helpers: the layout stamps the active locale onto
// window.__LOCALE__ before any client bundle runs.
export function browserLocale(): Locale {
  const value = typeof window !== 'undefined' ? (window as { __LOCALE__?: unknown }).__LOCALE__ : undefined;
  return isLocale(value) ? value : 'en';
}

export function tr<T>(en: T, es: T): T {
  return localized(browserLocale(), en, es);
}

export function browserDateLocale(): string {
  return dateLocale(browserLocale());
}
